/* eslint-disable */
import { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { useSearchParams } from "react-router-dom";
import {
  useApproveRequestMutation,
  useGetTransferRequestMoreDetailsQuery,
  useUpdateDelegationForAllTypesMutation,
  // useUpdateDelegationMutation,
  useUpdateRequestMutation,
  useUpdateMissingPunchStatusMutation,
} from "apis/services/requests";
import { RequestStatusValues } from "constants/Requests";
import {
  BOUNDARY_LABELS,
  PunchBoundary,
  PunchBoundaryKey,
} from "apis/types/timekeeper";
import { RequestProps } from "apis/types/requests";
import AVATAR_LOGO from "assets/images/user-Avata.png";
import TextInput from "components/inputs/TextInput";
import Button from "components/general/Button";
import Paper from "components/general/PaperComponent";
import UserInfo from "components/cards/UserInfo";
import Image from "components/general/Image";
import Text from "components/general/Text";
import DebouncedSearch from "components/inputs/DebouncedSearch";
import StatusBadge from "components/general/StatusBadge";
import showSuccessMsg from "utils/showSuccessMsg";
import InfoRow from "components/general/InfoRow";
import { ManageRequestProps } from "./types";
import { IoMdCheckmark } from "react-icons/io";
import ModalWrapper from "../ModalWrapper";
import CreateRequest from "../requests";
import { useGetAllStaffQuery } from "apis/services/staff";
import styles from "./styles.module.scss";
import COLORS from "constants/Colors";
import { Spin } from "antd";
import HandleErrors from "utils/handleErrors";
import { useSelector } from "react-redux";
import { RootState } from "reducers";
import { useGetProjectsQuery } from "apis/services/project";
import useGetUserInfo from "hooks/useGetUserInfo";
import { useGetZonesQuery } from "apis/services/locations";
import usePermissions from "hooks/usePermission";

// const RequestsType = {
//   0: "mission_requests",
//   1: "transfer_requests",
//   2: "leave_requests",
//   3: "business_trip_requests",
//   4: "business_permission_requests",
// };

function MemberUpdatedAt({ user }: { user: any }) {
  const locations = user?.created_at;
  if (locations) {
    return (
      <Text className={styles.memberUpdatedDate}>
        {moment(locations).format("MMM DD, YYYY")} at{" "}
        {moment(locations).format("hh:mm a")}
      </Text>
    );
  }
  return <> </>;
}

const getBoundaryLabel = (boundary: RequestProps["boundary"]) => {
  if (boundary === PunchBoundary.CheckIn) return BOUNDARY_LABELS.check_in;
  if (boundary === PunchBoundary.CheckOut) return BOUNDARY_LABELS.check_out;
  return BOUNDARY_LABELS[boundary as PunchBoundaryKey] ?? "--";
};

// A missing punch is a single punch, so start_date carries its exact datetime
// (end_date just mirrors it).
const formatRequestedAt = (request: RequestProps) => {
  const value =
    request?.start_date ||
    (request?.date && request?.time ? `${request.date} ${request.time}` : "");
  if (!value) return "--";
  const parsed = moment(value, [
    "YYYY-MM-DD HH:mm:ss",
    "YYYY-MM-DD HH:mm",
    moment.ISO_8601,
  ]);
  return parsed.isValid() ? parsed.format("MMM D, YYYY [at] h:mm A") : value;
};

export default function ManageRequest({
  containerStyle,
  isVisible,
  setIsVisible,
  request,
  transferAllocations,
  is_my_request,
}: ManageRequestProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [requestCancelation, setRequestCancelation] = useState(false);
  const [reason, setReason] = useState("");
  const [isEditModalVisible, setisEditModalVisible] = useState("");
  const [showdelegate, setshowdelegate] = useState(false);
  const [delegateSelection, setdelegateSelection] = useState({});
  const userData = useSelector((state: RootState) => state.auth.userData);
  const { hasPermissionTo } = usePermissions();
  const managePermission = {
    canCancelRequest: hasPermissionTo("allowed_to_cancel_request"),
    delegateRequests: hasPermissionTo("delegate_requests"),
    approveRejectRequest: hasPermissionTo("approve_reject_requests"),
  }
  const { role, userInfo } = useGetUserInfo();
  const { data: zones } = useGetZonesQuery({ page_size: 100 });
  const { data: requestMoreDetails } = useGetTransferRequestMoreDetailsQuery(
    request,
    { skip: request.request_type !== "transfer" }
  );

  const { data: managers, isFetching: managersLoader } = useGetAllStaffQuery({
    page_size: 100,
    page: Number(searchParams.get("managers_page") ?? "1"),
    search: searchParams.get("managers_search") ?? "",
    employment_type: 1,
    role: "manager",
  });
  let filterMangerns = managers?.results?.filter((manager: any) => {
    return manager.id !== userData?.id;
  });

  const { data: projects } = useGetProjectsQuery(
    {
      location: request?.location || requestMoreDetails?.location?.id,
      page_size: 100,
    },
    {
      skip: !request?.location || !requestMoreDetails?.location,
    }
  );

  const adminsOptions = managers?.results?.map((item: any) => ({
    label: `${item?.user?.username} (${item?.cfm_id})`,
    value: item?.id,
  }));
  const zoneOptions = zones?.results?.map((item: any) => ({
    label: item.name,
    value: item.id,
  }));
  const projectsOptions = projects?.results?.map((item: any) => ({
    label: item.name,
    value: item.id,
    projectId: item.project_id,
    categoryId: item?.category?.name,
  }));

  const getReportToName = (value: string) =>
    adminsOptions?.find((item) => +value === item.value)?.label;
  const getZoneName = (value: string) =>
    zoneOptions?.find((item: any) => +value === +item.value)?.label;
  const getProjectIdName = (value: string) =>
    projectsOptions?.find((item: any) => +value === item.value)?.projectId;
  const getCategoryNameFromProject = (projectId: string) =>
    projectsOptions?.find((item: any) => +projectId === item.value)?.categoryId;

  const [updateDelegation, { isLoading: delegationLoading }] =
    useUpdateDelegationForAllTypesMutation();
  const [updateRequest, { isLoading: updateLoading }] =
    useUpdateRequestMutation();
  const [acceptRequest, { isLoading: acceptLoading }] =
    useApproveRequestMutation();
  const [updateMissingPunchStatus, { isLoading: missingPunchLoading }] =
    useUpdateMissingPunchStatusMutation();

  // Every action button already reads isLoading, so fold the missing punch
  // status call into it rather than threading a second flag through them all.
  const isLoading = updateLoading || missingPunchLoading;

  // Missing punch approval goes through a status PATCH instead of the
  // approve_request / reject_request actions the other request types use.
  // The details response spells the type "missing punch", other payloads use
  // the "missing_punch" key.
  const isMissingPunch = ["missing_punch", "missing punch"].includes(
    request?.request_type
  );

  const setMissingPunchStatus = (status: number, msg: string) =>
    updateMissingPunchStatus({ id: request?.id, status })
      .unwrap()
      .then(() => {
        showSuccessMsg({ msg });
        setIsVisible(false);
      })
      .catch(() => {});

  useEffect(() => {
    setRequestCancelation(false);
    if (isVisible == false) {
      setshowdelegate(false);
      searchParams.set("managers_search", "");
      searchParams.set("managers_page", "1");
    }
  }, [isVisible]);

  const onDelegateRequest = () => {
    const requestValues = {
      // @ts-ignore
      id: request?.id,
      // @ts-ignore
      values: { delegated_to_id: delegateSelection?.id },
    };
    updateDelegation(requestValues)
      .unwrap()
      .then(() => {
        showSuccessMsg({ msg: "Request has been delegated successfully!" });
        searchParams.set("managers_search", "");
        setIsVisible(false);
      });
  };

  const onAccept = () => {
    if (isMissingPunch) {
      setMissingPunchStatus(
        RequestStatusValues.accepted,
        "Request has been accepted successfully!",
      );
      return;
    }
    acceptRequest({
      id: request?.id,
    })
      .unwrap()
      .then(() => {
        showSuccessMsg({ msg: "Request has been accepted successfully!" });
        setIsVisible(false);
      });
  };

  const onUpdateRequest = (request_type: string) => {
    if (isMissingPunch) {
      const isCancel = request_type === "cancel_request";
      setMissingPunchStatus(
        isCancel
          ? RequestStatusValues.cancelled
          : RequestStatusValues.rejected,
        `Request has been ${isCancel ? "cancelled" : "rejected"} successfully!`,
      );
      return;
    }
    updateRequest({
      id: request?.id,
      status: 0,
      type: is_my_request ? "list_my_requests" : "list_staff_requests",
      request_type,
      reason,
    })
      .unwrap()
      .then(() => {
        showSuccessMsg({ msg: "Request has been updated successfully!" });
        setIsVisible(false);
      });
  };

  const handleDelegateSelection = (item: any) => {
    console.log(item, "selection");
    // @ts-ignore
    if (item.id === delegateSelection?.id) {
      setdelegateSelection({});
    } else {
      setdelegateSelection(item);
    }
  };

  const onSearch = (value: string) => {
    searchParams.set("managers_search", value);
    searchParams.set("managers_page", "1");
    setSearchParams(searchParams);
  };

  const onEditRequest = (type: string) => {
    setisEditModalVisible(type);
  };

  const NormalRequestDetailsMarkup = (
    <div className="mb-4">
      <Paper
        backgroundColor="#F0F0F040"
        borderColor="#F0F0F040"
        paddingHorizontal={24}
        paddingVertical={18}
      >
        <div className="my-3">
          <Text
            className={`${styles.headerLbl}`}
          >{`Request Type: ${request?.request_type} ${request?.leave_type_name ? " - " + request?.leave_type_name : ""
            }`}</Text>
          <Text className="d-block">{request?.reason}</Text>
        </div>
        <div className="my-3">
          <Text className={`${styles.headerLbl}`}>Request Date:</Text>
          {request?.date && (
            <>
              {request?.from_time && (
                <Text className="d-block">
                  From: {request?.date} at {request?.from_time}
                </Text>
              )}
              {request?.from_time && (
                <Text className="d-block">
                  To: {request?.date} at {request?.to_time}
                </Text>
              )}
            </>
          )}
          {request?.start_date && (
            <Text className="d-block">From: {request?.start_date}</Text>
          )}
          {request?.end_date && (
            <Text className="d-block">To: {request?.end_date}</Text>
          )}
        </div>
      </Paper>
    </div>
  );

  const missingPunchRows = [
    { title: "Punch Type:", info: getBoundaryLabel(request?.boundary) },
    { title: "Requested At:", info: formatRequestedAt(request) },
    { title: "Reason:", info: request?.reason || "--" },
  ];

  const MissingPunchDetailsMarkup = (
    <div className="mb-4">
      <Paper
        backgroundColor="#F0F0F040"
        borderColor="#F0F0F040"
        paddingHorizontal={24}
        paddingVertical={18}
      >
        {missingPunchRows.map(({ title, info }) => (
          <div key={title} className="my-2">
            <InfoRow
              title={title}
              info={info}
              titleFontWeight="600"
              infoFontWeight="600"
              titleColor="grey"
              containerStyle="justify-content-start"
            />
          </div>
        ))}
      </Paper>
    </div>
  );

  const renderRequestDetails = () => {
    if (isMissingPunch) return MissingPunchDetailsMarkup;
    if (transferAllocations?.length && request?.request_type === "transfer")
      return TransferAllocationMarkup;
    return NormalRequestDetailsMarkup;
  };

  console.log(
    transferAllocations,
    "transferAllocationstransferAllocationstransferAllocations"
  );

  const TransferAllocationMarkup = (
    <div className="mb-4">
      {transferAllocations?.map((item) => (
        <Paper
          paddingHorizontal={24}
          paddingVertical={18}
          backgroundColor="#F0F0F040"
          borderColor="#D9D9D980"
        >
          <div className="mb-2">
            <InfoRow
              title="Project ID:"
              infoFontWeight="700"
              titleFontWeight="700"
              titleFontSize={24}
              infoFontSize={24}
              // titleColor={"grey"}
              // @ts-ignore
              info={getProjectIdName(item?.project) || "--"}
              containerStyle="justify-content-start"
            />
          </div>

          <InfoRow
            title="Start Date:"
            infoFontWeight="600"
            titleFontWeight="600"
            titleColor={"grey"}
            info={moment(request.start_date).format("DD-MM-YYYY") ?? "--"}
            containerStyle="justify-content-start"
          />
          <InfoRow
            title="Zone:"
            infoFontWeight="600"
            titleFontWeight="600"
            titleColor={"grey"}
            info={
              // @ts-ignore
              getZoneName(requestMoreDetails?.zone) ||
              // @ts-ignore
              requestMoreDetails?.zone?.name ||
              "--"
            }
            containerStyle="justify-content-start"
          />
          <InfoRow
            title="Locations:"
            infoFontWeight="600"
            titleFontWeight="600"
            titleColor={"grey"}
            // @ts-ignore
            info={
              requestMoreDetails?.location
                ?.map((item: any) => item.name)
                .join() || "--"
            }
            containerStyle="justify-content-start"
          />
          <InfoRow
            title="Report To:"
            infoFontWeight="600"
            titleFontWeight="600"
            titleColor={"grey"}
            info={
              // @ts-ignore
              getReportToName(
                // @ts-ignore
                item?.new_reports_to || requestMoreDetails?.new_reports_to
              ) || "--"
            }
            containerStyle="justify-content-start"
          />
          <InfoRow
            title="Category ID:"
            infoFontWeight="600"
            titleFontWeight="600"
            titleColor={"grey"}
            info={
              getCategoryNameFromProject(`${item.project}`) ||
              // @ts-ignore
              requestMoreDetails?.category_name ||
              "--"
            }
            containerStyle="justify-content-start"
          />
          <InfoRow
            title="Percentage:"
            infoFontWeight="600"
            titleFontWeight="600"
            titleColor={"grey"}
            info={`${item?.percentage}%` || "--"}
            containerStyle="justify-content-start"
          />
        </Paper>
      ))}
    </div>
  );

  return (
    <ModalWrapper
      size="large"
      headerTitle={
        showdelegate
          ? "Delegate"
          : isMissingPunch
            ? "Missing Punch Request"
            : request?.request_type ?? ""
      }
      isVisible={isVisible}
      setIsVisible={setIsVisible}
      isFooterHidden
    >
      <div className={`${styles.container} ${containerStyle}`}>
        {showdelegate ? (
          <div className="mb-4">
            <DebouncedSearch
              defaultValue={searchParams.get("managers_search") ?? ""}
              onSearchChange={onSearch}
              placeholder="Search for Members"
            />
            {managersLoader ? (
              <div className="d-flex my-5">
                <Spin className="m-auto" />
              </div>
            ) : (
              <>
                <div>
                  {filterMangerns?.map((manager) => (
                    <div
                      className="d-flex align-items-center justify-content-between pointer p-4 px-5 my-3"
                      style={{
                        borderRadius: 20,
                        border: `1px solid ${
                          // @ts-ignore
                          manager.id == delegateSelection?.id
                            ? COLORS.primary
                            : COLORS.lightGrey
                          }`,
                      }}
                      onClick={() => handleDelegateSelection(manager)}
                    >
                      <UserInfo data={manager} />
                      {/* @ts-ignore */}
                      {manager.id == delegateSelection?.id && (
                        <IoMdCheckmark size={36} color={COLORS.primary} />
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
            <div className="m-auto mt-4 w-25">
              {managePermission.delegateRequests &&
                <Button
                  btnClassName="m-auto w-100 justify-content-center"
                  // @ts-ignore
                  disabled={!delegateSelection?.id}
                  isLoading={delegationLoading}
                  onClick={onDelegateRequest}
                >
                  Delegate
                </Button>
              }
            </div>
          </div>
        ) : requestCancelation ? (
          <div className="pt-5">
            <div className="pb-5 text-center">
              <Text className={styles.headerTitle}>
                Are you sure you want to Cancel {request?.request_type} Request
                {isMissingPunch ? (
                  <> at “{formatRequestedAt(request)}”</>
                ) : (
                  <>
                    {" "}from “{request?.start_date || request?.date} - to{" "}
                    {request?.end_date || request?.date}”
                  </>
                )}
              </Text>
            </div>

            <TextInput
              value={reason}
              onChange={(e) => setReason(e.target?.value)}
              placeholder="Reason “Option”"
              type="textarea"
            />

            <div className={`mt-5 ${styles.buttonsGroup}`}>
              <Button
                isLoading={isLoading}
                onClick={() => onUpdateRequest("cancel_request")}
              >
                Confirm
              </Button>
              <Button
                isOutline
                isLoading={isLoading}
                onClick={() => setIsVisible(false)}
              >
                Keep the request
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className={`${styles.memberContent} mt-5 mb-4`}>
              <Paper
                backgroundColor="#F0F0F040"
                borderColor="#D9D9D980"
                paddingHorizontal={24}
                paddingVertical={18}
              >
                <div className={styles.memberInfo}>
                  <div className={styles.avatar}>
                    <Image src={AVATAR_LOGO} alt="User Avatar" />
                  </div>
                  <div>
                    {!is_my_request ? (
                      <>
                        <Text
                          className={styles.memberName}
                        >{`${request?.created_for?.user?.first_name} ${request?.created_for?.user?.last_name} Sent ${request?.request_type} Request`}</Text>
                        <Text
                          className={styles.memberUpdatedDate}
                          style={{ display: "block" }}
                        >
                          cfm id: {request.created_for.cfm_id}
                        </Text>
                        <Text
                          className={styles.memberUpdatedDate}
                          style={{ display: "block" }}
                        >
                          request id: {request.id}
                        </Text>
                      </>
                    ) : (
                      <>
                        <Text
                          className={styles.memberName}
                        >{`You have Sent ${request?.request_type} Request`}</Text>
                        <Text
                          className={styles.memberUpdatedDate}
                          style={{ display: "block" }}
                        >
                          request id: {request.id}
                        </Text>
                      </>
                    )}

                    <MemberUpdatedAt user={request} />
                  </div>
                </div>
                <StatusBadge
                  color={request?.status === "accepted" ? "success" : "warning"}
                  text={request?.status}
                  containerStyle={styles.statusBadge}
                />
              </Paper>
            </div>

            {!request?.changes_applied && is_my_request && (
              <div className={`${styles.requested_to} mb-4`}>
                <Paper
                  backgroundColor="#F0F0F040"
                  borderColor="#F0F0F040"
                  paddingHorizontal={24}
                  paddingVertical={18}
                >
                  <UserInfo data={request?.created_by} hideImage />
                  <MemberUpdatedAt user={request?.created_by} />
                </Paper>
              </div>
            )}

            {renderRequestDetails()}

            <div className={styles.buttonsGroup}>
              {["pending", "accepted"].includes(
                request?.status ? request?.status : ""
              ) &&
                (!is_my_request &&
                  request?.status === "pending" &&
                  (!request?.delegated_to.length ||
                    request?.delegated_to.at(-1) === userInfo?.id) ? (
                  <>
                    {managePermission.approveRejectRequest &&
                      <Button
                        isLoading={isLoading || acceptLoading}
                        onClick={() => onAccept()}
                      >
                        Accept
                      </Button>
                    }
                    {managePermission.delegateRequests &&
                      <Button
                        isOutline
                        isLoading={isLoading}
                        onClick={() => setshowdelegate(true)}
                      >
                        Delegate
                      </Button>
                    }
                    {["transfer", "mission"].includes(
                      request?.request_type || ""
                    ) && (
                        <Button
                          isOutline
                          isLoading={isLoading}
                          onClick={() => onEditRequest(request?.request_type)}
                        >
                          Edit
                        </Button>
                      )}
                    {managePermission.approveRejectRequest &&
                      <Button
                        isOutline
                        isLoading={isLoading}
                        onClick={() => onUpdateRequest("reject_request")}
                        btnClassName="border-0"
                      >
                        Reject
                      </Button>
                    }

                    <CreateRequest
                      isVisible={!!isEditModalVisible}
                      setIsVisible={setisEditModalVisible}
                      type={isEditModalVisible}
                      isUpdate
                      request={{
                        ...(requestMoreDetails || request),
                        transfer_allocations: transferAllocations,
                      }}
                      managers={managers}
                    />
                  </>
                ) : (
                  is_my_request &&
                  request?.status !== "accepted" && managePermission.canCancelRequest && (
                    <Button
                      isOutline
                      isLoading={isLoading}
                      onClick={() => setRequestCancelation(true)}
                    >
                      Cancel {request?.request_type}
                    </Button>
                  )
                ))}
            </div>
          </>
        )}
      </div>
    </ModalWrapper>
  );
}
